import { Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { debounceTime, map } from "rxjs/operators";
import { Dictionary } from "@helpers/types";
import { convertArrayToDictionary } from "@helpers/functions";
import { CryptocoinService } from "@main-services/currency";
import { MetalService } from "@main-services/currency/metal.service";
import { IndexService } from "@main-services/currency/index.service";
import { StockService } from "@main-services/currency/stock.service";
import { EtfService } from "@main-services/currency/etf.service";
import { EtcService } from "@main-services/currency/etc.service";
import { Tradable as OldTradable } from "@models/interfaces/tradable";
import { Tradable } from "../entities/tradable";
import { TradablesFactory } from "../infrastructure/tradables.factory";
import { LeveragedTokenService } from "@main-services/currency/leveragedToken.service";
import { SecurityTokenService } from "@main-services/currency/securityToken.service";
import { TRADABLES_TYPE } from "@helpers/constants";
import { TradableSettings } from "../entities/tradable-settings";
import { TradablePrice } from "../entities/tradable-price";
import { TradableInfo } from "../entities/tradable-info";
import { AssetSettings as OldAssetSettings } from "@models/tradable/split/asset_settings";
import { AssetPrice as OldAssetPrice } from "@models/tradable/split/asset_price";
import { AssetInfo as OldAssetInfo } from "@models/tradable/split/asset_info";

const tradablesToHash$ = (tradables$: Observable<OldTradable[]>): Observable<Dictionary<Tradable>> =>
	tradables$.pipe(map((tradables) => convertArrayToDictionary(adaptOldTradables(tradables), "id")));

const adaptOldTradables = (tradables: OldTradable[]): Tradable[] =>
	tradables.map((tradable) => TradablesFactory.createTradable(tradable));

@Injectable({
	providedIn: "root"
})
export class TradablesBridgeService {
	public readonly tradables$: Observable<Dictionary<Tradable>> = combineLatest([
		tradablesToHash$(this.cryptocoinService.getList()),
		tradablesToHash$(this.metalService.getList()),
		tradablesToHash$(this.indexService.getList()),
		tradablesToHash$(this.stockService.getList()),
		tradablesToHash$(this.etfService.getList()),
		tradablesToHash$(this.etcService.getList()),
		tradablesToHash$(this.leveragedTokenService.getList()),
		tradablesToHash$(this.securityTokenService.getList())
	]).pipe(
		debounceTime(0),
		map(([cryptocoins, metals, index, stocks, etfs, etcs, leveragedTokens, securityTokens]) => ({
			...cryptocoins,
			...metals,
			...index,
			...stocks,
			...etfs,
			...etcs,
			...leveragedTokens,
			...securityTokens
		}))
	);

	constructor(
		private readonly cryptocoinService: CryptocoinService,
		private readonly metalService: MetalService,
		private readonly indexService: IndexService,
		private readonly stockService: StockService,
		private readonly etfService: EtfService,
		private readonly etcService: EtcService,
		private readonly leveragedTokenService: LeveragedTokenService,
		private readonly securityTokenService: SecurityTokenService
	) {}

	//duplication of adaptors because the class based adaptor doesn't work
	public adaptOldTradables(tradables: OldTradable[]): Tradable[] {
		return tradables.map((tradable) => {
			const assetSettings = this.adaptAssetSettings(tradable.getAssetSettings());
			const assetPrice = this.adaptAssetPrice(tradable.getAssetPrices());
			const assetInfo = this.adaptAssetInfo(tradable.getAssetInfo());

			return {
				id: tradable.getId(),
				pid: tradable.getPid(),
				name: tradable.getName(),
				type: tradable.getCurrencyType() as TRADABLES_TYPE,
				assetGroupName: tradable.getAssetGroupName(),
				color: tradable.getColor(),
				isToken: tradable.isToken(),
				logo: tradable.getLogo(),
				logoDark: tradable.getLogoDark(),
				defaultLogo: tradable.getDefaultLogo(),
				precisionForCoins: tradable.getPrecision(),
				precisionForFiatPrice: tradable.getPrecisionFiat(),
				precisionForTx: tradable.getPrecisionForTx(),
				symbol: tradable.getSymbol(),
				displaySymbol: tradable.getSymbol(false, true),
				assetSettings: assetSettings,
				assetPrice: assetPrice,
				assetInfo: assetInfo
			};
		});
	}

	private adaptAssetSettings(settings: OldAssetSettings): TradableSettings {
		return {
			id: settings?.getId() || "",
			isAvailable: settings?.isAvailable() || false,
			isBuyActive: settings?.isBuyActive() || false,
			isDepositActive: settings?.isDepositActive() || false,
			isIndexOnly: settings?.isIndexOnly() || false,
			isMaintenanceEnabled: settings?.isMaintenance() || false,
			minBuyEur: settings?.getMinBuy() || 0,
			minSellEur: settings?.getMinSell() || 0,
			isSellActive: settings?.isSellActive() || false,
			sort: settings?.getSort() || 0,
			isTransferActive: settings?.isTransferActive() || false,
			walletMinDeposit: settings?.getWalletMinDeposit() || 0,
			walletSmallDeposit: settings?.getSmallDeposit() || 0,
			walletSmallDepositFee: settings?.getSmallDepositFee() || 0,
			isWithdrawActive: settings?.isWithdrawActive() || false,
			isPriceOrderAvailable: settings?.isPriceOrderAvailable() || false
		};
	}

	private adaptAssetPrice(price: OldAssetPrice): TradablePrice {
		return {
			id: price.getId(),
			circulatingSupply: price.getCirculatingSupply(),
			price: price.getPrice(),
			priceAmountChanges: {
				day: price.getChange24hAmount(),
				week: price.getChange1wAmount(),
				month: price.getChange1mAmount(),
				year: price.getChange1yAmount(),
				six_months: price.getChange6mAmount(),
				five_years: price.getChange5yAmount()
			},
			pricePercentageChanges: {
				day: price.getChange24h(),
				week: price.getChange1w(),
				month: price.getChange1m(),
				year: price.getChange1y(),
				six_months: price.getChange6m(),
				five_years: price.getChange5y()
			},
			indexAssets: []
		};
	}

	private adaptAssetInfo(info: OldAssetInfo): TradableInfo {
		return {
			id: info?.getId() || "",
			info: info?.getInfo() || "",
			description: info?.getDescription() || "",
			extraInfo: info?.getExtraInfo() || "",
			infoIntegration: info?.getInfoIntegration() || ""
		};
	}
}
